import Button from "@rio-cloud/rio-uikit/Button"
import ButtonDropdown from "@rio-cloud/rio-uikit/ButtonDropdown"
import DatePicker from "@rio-cloud/rio-uikit/DatePicker"
import { MenuItemProps } from "@rio-cloud/rio-uikit/MenuItem"
import Select from "@rio-cloud/rio-uikit/Select"
import TableSearch from "@rio-cloud/rio-uikit/TableSearch"
import TableToolbar from "@rio-cloud/rio-uikit/TableToolbar"
import moment, { Moment } from "moment"
import * as React from "react"
import { useEffect, useMemo, useRef } from "react"
import { FormattedMessage, useIntl } from "react-intl"
import { Shipment, TransportOrderType } from "@/model/transportorder/transportOrder"
import { InboundCSVExportButtonDropdown } from "./CSVExport/InboundCSVExportButtonDropdown"
import { InboundDateFilterType } from "./inboundTransportOrderUrlState"

const SEARCH_DEBOUNCE_DELAY_IN_MS = 250

const DATE_FILTER_OPTIONS: Array<{ value: InboundDateFilterType, labelId: string }> = [
    { value: InboundDateFilterType.CREATED_AT, labelId: "inboundTransportOrders.toolbar.dateFilter.documentDate" },
    { value: InboundDateFilterType.PICKUP_REQUESTED_AT, labelId: "inboundTransportOrders.toolbar.dateFilter.requestedPickupAtShipFrom" },
    { value: InboundDateFilterType.ARRIVAL_ESTIMATED_AT, labelId: "inboundTransportOrders.toolbar.dateFilter.estimatedArrivalAtCrossdock" },
    { value: InboundDateFilterType.DELIVERY_ESTIMATED_AT, labelId: "inboundTransportOrders.toolbar.dateFilter.calculatedArrivalAtShipTo" },
]

interface InboundTransportOrderToolbarProps {
    dateFilterType: InboundDateFilterType
    dateFrom: Date
    dateTo: Date
    orderType: TransportOrderType | undefined
    searchPhrase: string
    visibleRows: Shipment[]
    onChange: (change: Partial<{
        dateFilterType: InboundDateFilterType
        dateFrom: Date
        dateTo: Date
        orderType: TransportOrderType | undefined
        searchPhrase: string
    }>) => void
}

const ToolbarDatePicker = (props: {
    id: string
    label: React.ReactNode
    value: Date
    isValidDate: (date: Moment) => boolean
    onChange: (date: Date) => void
}) => {
    const intl = useIntl()
    return (
        <div className="table-toolbar-column">
            <label htmlFor={props.id}>{props.label}</label>
            <DatePicker
                id={props.id}
                locale={intl.locale}
                value={props.value}
                inputProps={{ id: props.id }}
                onChange={(date: Moment | string) => {
                    if (moment.isMoment(date) && date.isValid()) {
                        props.onChange(date.toDate())
                    }
                }}
                isValidDate={props.isValidDate}
                clearableInput={false}
                minWidth={200}
                timeFormat={false}
                mandatory
            />
        </div>
    )
}

export const InboundTransportOrderToolbar = (props: InboundTransportOrderToolbarProps) => {
    const {
        dateFilterType,
        dateFrom,
        dateTo,
        orderType,
        searchPhrase,
        onChange,
    } = props
    const searchInputRef = useRef<HTMLInputElement>(null)
    const committedSearchRef = useRef(searchPhrase)
    const latestSearchRef = useRef(searchPhrase)
    const searchTimeoutRef = useRef<number | undefined>(undefined)
    const intl = useIntl()
    useEffect(() => {
        committedSearchRef.current = searchPhrase
        latestSearchRef.current = searchPhrase
        window.clearTimeout(searchTimeoutRef.current)
        if (searchInputRef.current && searchInputRef.current !== document.activeElement) {
            searchInputRef.current.value = searchPhrase
        }
    }, [searchPhrase])

    useEffect(() => () => window.clearTimeout(searchTimeoutRef.current), [])

    const commitSearch = (value: string) => {
        window.clearTimeout(searchTimeoutRef.current)
        if (value !== committedSearchRef.current) {
            committedSearchRef.current = value
            onChange({ searchPhrase: value })
        }
    }

    const dateFilterItems: MenuItemProps[] = DATE_FILTER_OPTIONS.map(option => ({
        value: <FormattedMessage id={option.labelId}/>,
        active: option.value === dateFilterType,
        onSelect: () => onChange({ dateFilterType: option.value }),
    }))
    const activeDateFilterLabelId = DATE_FILTER_OPTIONS.find(option => option.value === dateFilterType)?.labelId
        ?? "inboundTransportOrders.toolbar.dateFilter.documentDate"
    const orderTypeOptions = useMemo(() => [
        {
            id: "ALL",
            label: intl.formatMessage({ id: "transportOrder.overview.toolbar.cargoTypeFilter.all" }),
        },
        {
            id: TransportOrderType.EMPTIES,
            label: intl.formatMessage({ id: "shipment.type.empties" }),
        },
        {
            id: TransportOrderType.MATERIAL,
            label: intl.formatMessage({ id: "shipment.type.materials" }),
        },
    ], [intl])
    const handleOrderTypeChange = (selectedOrderType: { id: string, label: string } | undefined) => {
        const selectedId = selectedOrderType?.id
        onChange({
            orderType: selectedId === TransportOrderType.EMPTIES || selectedId === TransportOrderType.MATERIAL
                ? selectedId
                : undefined,
        })
    }

    return (
        <TableToolbar>
            <div className="table-toolbar-container">
                <div className="table-toolbar-group-left">
                    <div className="table-toolbar-column">
                        <ButtonDropdown
                            data-testid="inbound-date-filter-type"
                            title={<span className="rioglyph rioglyph-settings"/>}
                            iconOnly
                            items={dateFilterItems}
                        />
                    </div>
                    <ToolbarDatePicker
                        id="inbound-transport-orders-date-from"
                        label={<>
                            <FormattedMessage id={activeDateFilterLabelId}/>{" "}
                            <FormattedMessage id="inboundTransportOrders.toolbar.from"/>:
                        </>}
                        value={dateFrom}
                        onChange={date => onChange({ dateFrom: moment(date).startOf("day").toDate() })}
                        isValidDate={date => date.isSameOrBefore(moment(dateTo), "day")}
                    />
                    <ToolbarDatePicker
                        id="inbound-transport-orders-date-to"
                        label={<><FormattedMessage id="inboundTransportOrders.toolbar.to"/>:</>}
                        value={dateTo}
                        onChange={date => onChange({ dateTo: moment(date).endOf("day").toDate() })}
                        isValidDate={date => date.isSameOrAfter(moment(dateFrom), "day")}
                    />
                    <div className="table-toolbar-column">
                        <Button
                            iconName="rioglyph-calendar-today"
                            onClick={() => {
                                const today = moment()
                                onChange({
                                    dateFrom: today.clone().startOf("day").toDate(),
                                    dateTo: today.clone().endOf("day").toDate(),
                                })
                            }}
                        >
                            <FormattedMessage id="inboundTransportOrders.toolbar.today"/>
                        </Button>
                    </div>
                </div>
                <div className="table-toolbar-group-right">
                    <div className="table-toolbar-column">
                        <InboundCSVExportButtonDropdown
                            visibleRows={props.visibleRows}
                            dateFrom={dateFrom}
                            dateTo={dateTo}
                        />
                    </div>
                    <div className="table-toolbar-column">
                        <Select
                            id="inbound-order-type-filter"
                            name="inbound-order-type-filter"
                            options={orderTypeOptions}
                            value={orderType ? [orderType] : ["ALL"]}
                            label={intl.formatMessage({
                                id: "transportOrder.overview.toolbar.transportAndCargoTypeFilter.label",
                            })}
                            onChange={handleOrderTypeChange}
                        />
                    </div>
                    <div className="table-toolbar-column">
                        <TableSearch
                            placeholder={intl.formatMessage({ id: "inboundTransportOrders.toolbar.searchPlaceholder" })}
                            defaultValue={searchPhrase}
                            inputRef={searchInputRef as React.RefObject<HTMLInputElement>}
                            onChange={value => {
                                latestSearchRef.current = value
                                window.clearTimeout(searchTimeoutRef.current)
                                if (value === committedSearchRef.current) {
                                    return
                                }
                                searchTimeoutRef.current = window.setTimeout(() => {
                                    commitSearch(value)
                                }, SEARCH_DEBOUNCE_DELAY_IN_MS)
                            }}
                            onBlur={() => {
                                commitSearch(latestSearchRef.current)
                            }}
                        />
                    </div>
                </div>
            </div>
        </TableToolbar>
    )
}
